import { useState, useCallback } from 'react';

interface BaseEntity {
  id: string;
  created_at: string;
  is_deleted: boolean;
}

interface CRUDHookReturn<T extends BaseEntity> {
  items: T[];
  loading: boolean;
  add: (data: Omit<T, keyof BaseEntity>) => void;
  edit: (id: string, updates: Omit<T, keyof BaseEntity>) => void;
  remove: (id: string) => void;
  getById: (id: string) => T | undefined;
  getActive: () => T[];
}

export const useCRUD = <T extends BaseEntity>(
  storageKey: string,
  defaultItems: T[] = []
): CRUDHookReturn<T> => {
  const [items, setItems] = useState<T[]>(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      return JSON.parse(saved);
    }
    if (defaultItems.length > 0) {
      localStorage.setItem(storageKey, JSON.stringify(defaultItems));
      return defaultItems;
    }
    return [];
  });

  const [loading, setLoading] = useState(false);

  const saveToStorage = useCallback((updatedItems: T[]) => {
    localStorage.setItem(storageKey, JSON.stringify(updatedItems));
    setItems(updatedItems);
  }, [storageKey]);

  const add = useCallback((data: Omit<T, keyof BaseEntity>) => {
    setLoading(true);
    const newItem = {
      ...data,
      id: Date.now().toString(),
      created_at: new Date().toISOString(),
      is_deleted: false,
    } as T;
    
    const updatedItems = [...items, newItem];
    saveToStorage(updatedItems);
    setLoading(false);
  }, [items, saveToStorage]);

  const edit = useCallback((id: string, updates: Omit<T, keyof BaseEntity>) => {
    setLoading(true);
    const updatedItems = items.map(item =>
      item.id === id ? { ...item, ...updates } : item
    );
    saveToStorage(updatedItems);
    setLoading(false);
  }, [items, saveToStorage]);

  const remove = useCallback((id: string) => {
    setLoading(true);
    const updatedItems = items.filter(item => item.id !== id);
    saveToStorage(updatedItems);
    setLoading(false);
  }, [items, saveToStorage]);

  const getById = useCallback((id: string) => {
    return items.find(item => item.id === id);
  }, [items]);

  const getActive = useCallback(() => {
    return items.filter(item => !item.is_deleted);
  }, [items]);

  return {
    items,
    loading,
    add,
    edit,
    remove,
    getById,
    getActive,
  };
};